import { type MouseEvent, useRef } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { ANCHOR_NAV_LABEL } from './labels'
import type { AnchorItem } from './types'
import { useActiveAnchor } from './use-active-anchor'
import { anchorLinkVariants } from './variants'

export type AnchorProps = {
  items: AnchorItem[]
  className?: string
}

/**
 * Anchor is used as table of contents navigation element, expected to be located at the left side of the screen showing what Section of the page the user is currently viewing.
 * It also allows navigating to the different sections using Web Anchors (the URL value after the hashtag.)
 * For navigation to work, make sure a React element with the matching `id` field is present in the page.
 */
export const Anchor = ({ items, className }: AnchorProps) => {
  const navRef = useRef<HTMLElement>(null)
  const { activeId, scrollToSection } = useActiveAnchor(
    items.map(({ id }) => id),
    navRef,
  )

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault()
    scrollToSection(id)
    window.history.replaceState(null, '', `#${encodeURIComponent(id)}`)
  }

  return (
    <nav
      ref={navRef}
      aria-label={ANCHOR_NAV_LABEL}
      className={cn('sticky top-24 w-full border-l-2 border-main-border-default', className)}
    >
      {items.map(({ id, title }) => (
        <a
          key={id}
          href={`#${encodeURIComponent(id)}`}
          aria-current={id === activeId ? 'location' : undefined}
          className={anchorLinkVariants({ active: id === activeId })}
          onClick={(event) => handleClick(event, id)}
        >
          {title}
        </a>
      ))}
    </nav>
  )
}
